import { useContent } from "../../../hooks/useContent";
import { GetDefaultFooter } from "../../../queries/GetDefaultFooter";
import { ThemeContextProvider } from "../../providers/ThemeContextProvider";
import { Link } from "../core/Link";
import { NetflixAdsLogo } from "../NetflixAdsLogo";
import { useRef } from "react";
import { IconArrowUpRight } from "@hawkins/assets";
import { FooterMediaGrid } from "./FooterMediaGrid";
import { SimpleNewsletterSignupForm } from "./SimpleNewsletterSignupForm";
import { readFragment } from "gql.tada";
import { LinkFragment } from "../../../queries/fragments/LinkFragment";

export const Footer = () => {
  const [response] = useContent({ query: GetDefaultFooter });

  // The nav & footer have awkward overlap issues, so this ensures
  // that when the footer is in view, the header is hidden
  const footerRef = useRef<HTMLElement>(null);

  if (response.fetching || response.error) {
    return null;
  } else {
    const footer = response.data?.footerCollection?.items[0] ?? null;
    if (!footer) {
      console.error("Default footer has failed to resolve: ", response);
    }

    return (
      <ThemeContextProvider
        theme={{
          palette: "red",
          foreground: "neutral-light",
          background: "true",
        }}
        className="text-palette-current-foreground z-50"
      >
        <footer
          ref={footerRef}
          className="bg-palette-current-background flex flex-col gap-40 dsk:gap-52 container-base"
        >
          <div className="dsk:pt-24 pt-20 dsk:pb-12 flex flex-col justify-between gap-16 dsk:gap-[344px]">
            <div className="flex flex-col dsk:flex-row justify-between">
              <div>
                <NetflixAdsLogo
                  className="w-28 mb-7"
                  secondarySectionProps={{
                    className: "text-neutral-light/60",
                  }}
                />
                <SimpleNewsletterSignupForm footer={footer} />
              </div>
              <ul className="flex gap-y-12 gap-x-2 dsk:gap-4 flex-wrap">
                {footer?.linkGroupsCollection?.items.map((linkGroup, index) => (
                  <li key={index} className="flex-1">
                    <p className="text-body-small pb-5 dsk:pb-7">
                      {linkGroup?.title}
                    </p>
                    <ul className="flex flex-col gap-3">
                      {linkGroup?.linksCollection?.items.map((item, index) => {
                        const link = readFragment(LinkFragment, item);

                        if (!link) return null;

                        return (
                          <li key={index}>
                            <Link
                              link={link}
                              className="opacity-70 text-body-small font-normal"
                            >
                              {link?.text}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col-reverse gap-16 dsk:flex-row justify-between w-full">
              <p className="text-body-small">{footer?.copyright}</p>
              <ul className="flex gap-5 dsk:gap-10 flex-col dsk:flex-row">
                {footer?.bottomLinkGroup?.linksCollection?.items.map(
                  (item, index) => {
                    const link = readFragment(LinkFragment, item);
                    if (!link) return null;
                    return (
                      <li key={index}>
                        <Link
                          link={link}
                          className="text-body-regular dsk:text-body-small inline-flex gap-2"
                        >
                          {link?.text}
                          {link?.isExternal && (
                            <IconArrowUpRight size="small" />
                          )}
                        </Link>
                      </li>
                    );
                  }
                )}
                <li>
                  <button
                    className="appearance-none text-body-regular dsk:text-body-small"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onClick={() => (window as any).Optanon.ToggleInfoDisplay()}
                  >
                    {footer?.cookies}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex justify-center items-center w-full">
            <p className="text-display uppercase text-center max-w-7xl">
              {footer?.easterEggText}
            </p>
          </div>
          <div className="flex flex-col gap-20 dsk:gap-52">
            <FooterMediaGrid
              mediaItems={footer?.mediaCollection?.items ?? []}
            />
            <NetflixAdsLogo
              className="mb-10 dsk:mb-7 w-full"
              secondarySectionProps={{
                className: "text-neutral-light/60",
              }}
            />
          </div>
        </footer>
      </ThemeContextProvider>
    );
  }
};
